import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Compass, LayoutPanelLeft, Radar, Zap } from 'lucide-react';
import { cn } from '@/lib/cn';
import { operarPath, towerPath } from './operar/operate-types';

// The disruption's three renderings — Express (the foolproof one-flight board),
// Classic (the tabbed overview) and the Tower (the expert control room) — are
// three URLs over the same aggregate. This switcher is the ONE way between
// them, mounted on all three surfaces: without it a disruption declared via
// /express was unreachable in express form once the operator navigated away
// (deep links on the bare /disruptions/:urn land on the Tower).

export type DisruptionViewMode = 'operar' | 'express' | 'classic' | 'tower';

// The switcher offers Operar (the new UI, where a disruption always opens) and «Modo
// experto» (the Tower) — PO 2026-10-27. The classic overview is HIDDEN and the Express
// board retired; when the operator IS on one of them, its entry re-appears as the
// active pill so the switcher never orphans the current page.
const VISIBLE_MODES: readonly DisruptionViewMode[] = ['operar', 'tower'];

const MODE_ICON: Record<DisruptionViewMode, typeof Zap> = {
  operar: Compass,
  express: Zap,
  classic: LayoutPanelLeft,
  tower: Radar,
};

function disruptionViewPath(mode: DisruptionViewMode, urn: string): string {
  const id = encodeURIComponent(urn);
  switch (mode) {
    case 'operar':
      return operarPath(urn);
    case 'express':
      return `/express/${id}`;
    case 'classic':
      return `/disruptions/${id}/classic`;
    case 'tower':
      return towerPath(urn);
  }
}

export function DisruptionViewSwitcher({
  urn,
  current,
  className,
}: {
  urn: string;
  current: DisruptionViewMode;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <nav
      aria-label={t('disruptionViews.label')}
      className={cn(
        'inline-flex shrink-0 rounded-lg border border-border bg-muted/40 p-0.5',
        className,
      )}
      data-testid="disruption-view-switcher"
    >
      {(VISIBLE_MODES.includes(current) ? VISIBLE_MODES : [...VISIBLE_MODES, current]).map(
        (mode) => {
          const Icon = MODE_ICON[mode];
          const active = mode === current;
          return (
            <Link
              key={mode}
              to={disruptionViewPath(mode, urn)}
              // Stepping INTO express through the switcher (from the Tower or the
              // classic view) lands the board PAUSED: the operator came to look at
              // an existing disruption, not to ask the autopilot to start firing
              // steps under them. A fresh declare (which navigates without state)
              // still runs hands-off — that's the express promise.
              state={mode === 'express' ? { autopilot: 'paused' } : undefined}
              aria-current={active ? 'page' : undefined}
              title={t(`disruptionViews.${mode}Hint`)}
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11.5px] transition-colors',
                active
                  ? 'bg-background font-semibold text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              data-testid={`disruption-view-${mode}`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden />
              {t(`disruptionViews.${mode}`)}
            </Link>
          );
        },
      )}
    </nav>
  );
}
